import { LOADING_SHIMMER_SPEED } from "@components/Accesses/AccessDetails/config";
import { LOADING_SHIMMER_HIGHLIGHT_COLOR } from "@components/Accesses/AccessDetails/config";
import { LOADING_SHIMMER_BG_COLOR } from "@components/Accesses/AccessDetails/config";
import LBox, { LBoxProps } from "@components/LBox";
import LIcon, { LIconProps } from "@components/LIcon";
import LText, { Props as LTextProps } from "@components/LText";
import { theme } from "@theme";
import type { EventStartLocal } from "@libs/wire";
import SkeletonPlaceholder from "react-native-skeleton-placeholder";

type DateTimeProps = {
  startsAtLocal: EventStartLocal;
  endsAtLocal: EventStartLocal | null;
  boxProps?: LBoxProps;
  iconProps?: Partial<LIconProps>;
  textProps?: Omit<LTextProps, "children">;
};

// The API resolves the venue's wall clock into plain `{ date, time }` components
// (2026-07-23 convention) — render them literally, zero timezone work. Parse a
// bare `HH:MM` as a local time-of-day so the h:mm a formatting stays tz-free.
const formatLocalTime = (time: string) =>
  new Date(`2000-01-01T${time}`)
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

const DateTime = ({
  startsAtLocal,
  endsAtLocal,
  boxProps,
  iconProps,
  textProps,
}: DateTimeProps) => {
  // Parse the plain `YYYY-MM-DD` as a local date (append midnight) so the day
  // never shifts across viewer timezones.
  const dateLabel = new Date(
    `${startsAtLocal.date}T00:00:00`,
  ).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const startTime = startsAtLocal.time
    ? formatLocalTime(startsAtLocal.time)
    : null;
  const endTime =
    startTime && endsAtLocal?.time ? formatLocalTime(endsAtLocal.time) : null;
  return (
    <LBox flexDirection="row" alignItems="center" gap="s1_5" {...boxProps}>
      <LIcon
        colors={{ color: theme.colors.lightGrey }}
        name="calendar"
        size={16}
        {...iconProps}
      />
      <LText variant="Body/B1/Medium" color="lightGrey" {...textProps}>
        {dateLabel} {startTime ? <>• {startTime}</> : null}
        {endTime ? ` - ${endTime}` : null}
      </LText>
    </LBox>
  );
};

const Loading = () => {
  return (
    <LBox flexDirection="row" alignItems="center" gap="s1_5">
      <SkeletonPlaceholder
        highlightColor={LOADING_SHIMMER_HIGHLIGHT_COLOR}
        backgroundColor={LOADING_SHIMMER_BG_COLOR}
        speed={LOADING_SHIMMER_SPEED}
        borderRadius={theme.borderRadii.max}
      >
        <SkeletonPlaceholder.Item
          width={14}
          height={14}
          borderRadius={theme.borderRadii.max}
        />
      </SkeletonPlaceholder>
      <SkeletonPlaceholder
        highlightColor={LOADING_SHIMMER_HIGHLIGHT_COLOR}
        backgroundColor={theme.colors.whiteTranslucent20}
        speed={LOADING_SHIMMER_SPEED}
        borderRadius={theme.borderRadii.max}
      >
        <SkeletonPlaceholder.Item
          width={100}
          height={theme.textVariants["Body/B1/Medium"].lineHeight}
          borderRadius={theme.borderRadii.max}
        />
      </SkeletonPlaceholder>
    </LBox>
  );
};

export default {
  DateTime,
  Loading,
};
